import { Controller } from "@hotwired/stimulus";
import { insertRowFromTemplate, removeRow, updateSortKeys } from "../../helpers/row_list_helpers";

const T = (name) => `[data-admin--checkout-form-custom-fields-target='${name}']`;

export default class extends Controller {
  static targets = [
    "list",
    "template",
    "fieldRow",
    "sortKey",
    "destroyInput",
    "fieldTypeSelect",
    "placeholderField",
    "optionsEditor",
    "optionsList",
    "optionRow",
    "optionTemplate",
    "knownFieldSelect",
  ];

  static values = { nextIndex: Number };

  connect() {
    this._index = this.nextIndexValue || 0;
    this._syncKnownFieldOptions();
  }

  get nextIndex() {
    return this._index++;
  }

  addField(event) {
    event.preventDefault();
    insertRowFromTemplate(this.listTarget, this.templateTarget, this.nextIndex);
    this.updateSortKeys();
    this._markDirty();
  }

  addKnownField(event) {
    const code = event.target.value;
    event.target.value = "";
    if (!code || this._codeExists(code)) return;

    const template = this.element.querySelector(`[data-known-template='${code}']`);
    if (!template) return;

    insertRowFromTemplate(this.listTarget, template, this.nextIndex);
    this.updateSortKeys();
    this._syncKnownFieldOptions();
    this._markDirty();
  }

  removeField(event) {
    const row = event.target.closest(T("fieldRow"));
    if (!row) return;

    removeRow(row, T("destroyInput"));
    this.updateSortKeys();
    this._syncKnownFieldOptions();
    this._markDirty();
  }

  _codeExists(code) {
    return Array.from(this.listTarget.querySelectorAll("input[name$='[code]']")).some((input) => {
      const row = input.closest(T("fieldRow"));
      if (row && row.classList.contains("hidden")) return false;
      return input.value === code;
    });
  }

  _syncKnownFieldOptions() {
    if (!this.hasKnownFieldSelectTarget) return;

    this.knownFieldSelectTarget.querySelectorAll("option[value]").forEach((option) => {
      if (option.value === "") return;
      option.disabled = this._codeExists(option.value);
    });
  }

  fieldTypeChanged(event) {
    const row = event.target.closest(T("fieldRow"));
    if (!row) return;

    const fieldType = event.target.value;
    const placeholderField = row.querySelector(T("placeholderField"));
    const optionsEditor = row.querySelector(T("optionsEditor"));
    const optionsList = row.querySelector(T("optionsList"));

    if (placeholderField) {
      placeholderField.classList.toggle("hidden", fieldType !== "text");
    }

    if (optionsEditor) {
      optionsEditor.classList.toggle("hidden", fieldType !== "radio");
    }

    if (fieldType === "radio" && optionsList && optionsList.children.length === 0) {
      this.addOptionToList(row);
    }

    if (fieldType !== "radio" && optionsList) {
      optionsList.innerHTML = "";
    }
  }

  addOption(event) {
    event.preventDefault();
    const row = event.target.closest(T("fieldRow"));
    if (row) {
      this.addOptionToList(row);
      this._markDirty();
    }
  }

  removeOption(event) {
    const optionRow = event.target.closest(T("optionRow"));
    if (!optionRow) return;

    const fieldRow = event.target.closest(T("fieldRow"));
    if (!fieldRow) return;

    const optionsList = fieldRow.querySelector(T("optionsList"));
    if (optionsList && optionsList.querySelectorAll(T("optionRow")).length <= 1) return;

    optionRow.remove();
    this._markDirty();
  }

  optionChanged(event) {
    const optionRow = event.target.closest(T("optionRow"));
    if (optionRow) {
      const hiddenInput = optionRow.querySelector("input[type='hidden']");
      if (hiddenInput) {
        hiddenInput.value = event.target.value;
      }
    }
  }

  addOptionToList(row) {
    const optionsList = row.querySelector(T("optionsList"));
    const template = row.querySelector(T("optionTemplate"));
    if (!optionsList || !template) return;

    const prefix = this.getFieldPrefix(row);
    if (!prefix) return;

    const visibleOptions = optionsList.querySelectorAll(T("optionRow"));
    const optionIndex = visibleOptions.length + 1;

    const html = template.innerHTML.replace(/OPTION_PREFIX/g, prefix);
    optionsList.insertAdjacentHTML("beforeend", html);

    const newRow = optionsList.lastElementChild;
    const textInput = newRow.querySelector("input[type='text']");
    if (textInput) textInput.placeholder = `Option ${optionIndex}`;
  }

  getFieldPrefix(row) {
    const idInput = row.querySelector("input[name*='[id]']");
    if (idInput) {
      const match = idInput.name.match(/(.+)\[id\]/);
      if (match) return match[1];
    }
    return null;
  }

  updateSortKeys() {
    updateSortKeys(this.listTarget, T("fieldRow"), T("sortKey"));
  }

  _markDirty() {
    this._editorController?.markDirty();
  }

  get _editorController() {
    const el = this.element.closest("[data-controller~='admin--checkout-form-editor']");
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(el, "admin--checkout-form-editor");
  }
}
